<script setup lang="ts">
import { ref } from 'vue'
import { z } from 'zod'
import BModal from '@/components/common/BModal.vue'
import TextInput from '@/components/common/textInput.vue';
import BButton from '../common/bButton.vue';
import { userApi } from '@/services/api'
import { useUserStore } from '@/config/store/userStore'
import { handlePromiseStatus } from '@/services/application'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const isOpen = defineModel<boolean>('isOpen')
const changingPasswordStatus = ref(null)
const userStore = useUserStore()
const isDev = import.meta.env.MODE === 'development' || window.location.origin.includes('sandbox')
// const isDev = false

const oldPasswordError = ref('')
const oldPassword = ref('')
const newPassword = ref('')
const newPasswordError = ref('')
const confirmNewPassword = ref('')
const confirmNewPasswordError = ref('')

const passwordSchema = z.string()
  .min(8, t('modules.recruitment.changePassword.minLength'))
  .regex(/[a-z]/, t('modules.recruitment.changePassword.lowercase'))
  .regex(/[A-Z]/, t('modules.recruitment.changePassword.uppercase'))
  .regex(/\d/, t('modules.recruitment.changePassword.digit'))
  .regex(/[!@#$%^&*()\-_=+{};:,<.>/?]/, t('modules.recruitment.changePassword.special'))

const handleSubmit = () => {
  // Cleared on each try: an earlier check returning first must not leave the old mismatch shown
  confirmNewPasswordError.value = ''
  if (!newPassword.value) return;

  if (!userStore.user?.user?.toChangePassword && !oldPassword.value) {
    oldPasswordError.value = t('modules.recruitment.changePassword.enterCurrentPassword')
    return;
  }

  oldPasswordError.value = ''

  if (!isDev) {
    const result = passwordSchema.safeParse(newPassword.value)
    if (!result.success) {
      newPasswordError.value = result.error.issues[0].message
      return;
    }
  }

  newPasswordError.value = ''
  // Under the field to correct: the confirmation
  if (confirmNewPassword.value !== newPassword.value) {
    confirmNewPasswordError.value = t('modules.recruitment.changePassword.confirmationMismatch')
    return;
  }

  const userId = userStore.user?.user?._id
  if (!userId) {
    console.error('User ID is not defined in user store.')
    return
  }

  const changePasswordRequest = userApi.changeUserPassword(userId, { oldPassword: oldPassword.value, newPassword: newPassword.value })
  handlePromiseStatus(changePasswordRequest.promise, { loadingStatus: changingPasswordStatus })

  changePasswordRequest.promise.then(() => {
    if (userStore.user?.user) {
      userStore.user.user.toChangePassword = false
    }
    isOpen.value = false
    oldPassword.value = ''
    newPassword.value = ''
    confirmNewPassword.value = ''
  }).catch((error) => {
    console.error(error)
    newPasswordError.value = error?.response?.data?.customMessage || error?.response?.data?.message || error?.message || t('common.error')
  })
};

</script>

<template>
  <BModal
    v-model="isOpen"
    position="left"
    data-cy="change-password-modal"
    :disable-back-drop="userStore?.user?.user?.toChangePassword"
    :disable-close="userStore?.user?.user?.toChangePassword"
  >
    <template #header>
      <h2>
        {{ userStore.user?.user?.toChangePassword ? t('modules.recruitment.changePassword.firstTitle') : t('modules.recruitment.changePassword.title') }}
      </h2>
    </template>
    <template #default>
      <form data-cy="change-password-form" @submit.prevent="handleSubmit" class="flex flex-col gap-5 max-w-96">
        <p v-if="userStore.user?.user?.toChangePassword" class="subtitle text-sm">
          {{ t('modules.recruitment.changePassword.firstHint') }}
        </p>

        <TextInput v-if="!userStore.user?.user?.toChangePassword" :label="t('modules.recruitment.changePassword.currentPassword')" input-type="password" id="currentPassword" v-model="oldPassword"
          name="currentPassword" required autocomplete="current-password" :placeholder="t('modules.recruitment.changePassword.currentPassword')"
          :aria-label="t('modules.recruitment.changePassword.currentPassword')" :error-message="oldPasswordError" />

        <div class="flex flex-col gap-2">
          <TextInput input-type="password" id="newPassword" v-model="newPassword" required autocomplete="new-password"
            :label="t('modules.recruitment.changePassword.newPassword')" :placeholder="t('modules.recruitment.changePassword.newPassword')" :aria-label="t('modules.recruitment.changePassword.newPassword')" data-cy="change-password-new"
            :error-message="newPasswordError" />
          <p v-if="!isDev" class="text-xs subtitle italic">
            {{ t('modules.recruitment.changePassword.rules') }} <br> (ex: <span class="px-2 py-1 bg-fade rounded-sm font-mono not-italic">!@#$%^&*()-_+=<>?</span>).</p>
        </div>


        <TextInput input-type="password" id="confirmNewPassword" v-model="confirmNewPassword" name="confirmNewPassword"
          required autocomplete="new-password" :label="t('modules.recruitment.changePassword.confirmPassword')" :placeholder="t('modules.recruitment.changePassword.confirmPassword')"
          :aria-label="t('modules.recruitment.changePassword.confirmPassword')" data-cy="change-password-confirm" :error-message="confirmNewPasswordError" />

        <div class="flex items-center justify-end">
          <BButton type="submit" data-cy="change-password-submit" :loading-status="changingPasswordStatus">
            {{ t('modules.recruitment.changePassword.submit') }}
          </BButton>
        </div>
      </form>
    </template>
  </BModal>
</template>
